import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Checkbox } from '@sunwebgroup/uds-ui/checkbox'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import { getGolfSlotIdBeingEdited, useGolfServiceStore } from '@/scopes/checkout/components/services/GolfService/store'
import { updateEditingGolfSlotParticipants } from '@/scopes/checkout/domain/actions/services/golf'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { golfEditingValueForSlotSelector } from '../selectors'
import styles from './ParticipantsSelector.module.css'

export function ParticipantsSelector() {
  const intl = useIntl()
  const slotId = useGolfServiceStore(getGolfSlotIdBeingEdited)
  const { service, setServiceLocalValue } = useService(ServiceType.Golf)

  const slot = golfEditingValueForSlotSelector(service, slotId)

  if (!service || !slot || !slotId)
    return null

  const participantIds = Object.keys(service.participants)

  if (participantIds.length === 0)
    return null

  const selectedOption = slot.optionId ? service.options[slot.optionId] : null
  const pricePerPerson = selectedOption && !selectedOption.withBuggy ? selectedOption.price : 0

  const buggyOccupancy = selectedOption?.buggyOccupancy
  const selectedCount = slot.participantIds.length
  const showBuggyOccupancyWarning
    = selectedOption?.withBuggy
      && buggyOccupancy !== undefined
      && (selectedCount < buggyOccupancy.min || selectedCount > buggyOccupancy.max)

  function handleParticipantToggle(pid: string, checked: boolean) {
    if (!slotId)
      return
    const updated = checked
      ? [...(slot?.participantIds ?? []), pid]
      : (slot?.participantIds ?? []).filter(id => id !== pid)
    setServiceLocalValue(updateEditingGolfSlotParticipants({ slotId, participantIds: updated }))
  }

  return (
    <div className={styles.section}>
      <Typography variant="body-s-bold-flat">
        {intl.formatMessage({ id: 'checkout_steps_ancillaries_golf_participants_label' })}
      </Typography>
      <div className={styles.list}>
        {participantIds.map((pid) => {
          const participant = service.participants[pid]
          const checkboxId = `golf-participant-${slotId}-${pid}`
          const isChecked = slot.participantIds.includes(pid)

          return (
            <ListItem
              key={pid}
              as="label"
              htmlFor={checkboxId}
              variant="outlined"
              className={styles.participantItem}
            >
              <Checkbox
                id={checkboxId}
                checked={isChecked}
                onCheckedChange={checked => handleParticipantToggle(pid, Boolean(checked))}
              />
              <Typography variant="body-m-regular-tight" className={styles.participantName}>
                {participant?.name ?? pid}
              </Typography>
              {pricePerPerson > 0 && (
                <Typography variant="body-s-regular-tight" className={styles.participantPrice}>
                  {intl.formatCurrency(pricePerPerson, { showSign: true })}
                </Typography>
              )}
            </ListItem>
          )
        })}
      </div>
      {showBuggyOccupancyWarning && buggyOccupancy && (
        <Banner color="warning" size="M">
          <Banner.TextsWrapper>
            <Banner.Text>
              {buggyOccupancy.min === buggyOccupancy.max
                ? intl.formatMessage(
                    { id: 'checkout_steps_ancillaries_golf_buggy_occupancy_exact_warning' },
                    { count: buggyOccupancy.min },
                  )
                : intl.formatMessage(
                    { id: 'checkout_steps_ancillaries_golf_buggy_occupancy_range_warning' },
                    { min: buggyOccupancy.min, max: buggyOccupancy.max },
                  )}
            </Banner.Text>
          </Banner.TextsWrapper>
        </Banner>
      )}
    </div>
  )
}
